import { getAndLogStatSigDynamicConfig } from '@otter-ai/telemetry';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useExperimentEnabled } from '@/experiment/experiments-client';

type RolloutGroupPath = {
  name: string;
  pathPrefixes: string[];
};

let rolloutGroupPaths: RolloutGroupPath[] = [];

getAndLogStatSigDynamicConfig<{ rolloutGroupPaths: RolloutGroupPath[] }>(
  'nextjs_rollout_groups',
  {
    logDynamicConfig: false,
  },
).then((res) => {
  rolloutGroupPaths = res?.rolloutGroupPaths ?? [];
});

type LinkProps = React.ComponentProps<typeof Link>;

/**
 * A wrapper around the Next.js `Link` component.
 * This handles the logic to switch back to Angular when navigating between rollout groups.
 *
 * This does not handle all the use-cases for links.
 * For example, a later event could call `event.preventDefault()` again,
 * which if properly handled, this component would not reload the page.
 *
 * FYI: `prefetch={false}` is set during the initial rollout to Next.js
 * so we don't confuse ourselves when troubleshooting.
 */
export function RolloutGroupLink(linkProps: LinkProps) {
  const pathname = usePathname();
  const rolloutExp = useExperimentEnabled('nextjs_force_reload');

  if (!rolloutExp || linkProps.target) {
    // I'm assuming all `target` will open a new window
    return <Link prefetch={false} {...linkProps} />;
  }

  const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.defaultPrevented) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      // Assuming the user is trying to open the link in a new tab
      return;
    }

    linkProps.onClick?.(event);

    if (!linkProps.href) {
      return;
    }

    const href =
      typeof linkProps.href === 'string'
        ? new URL(linkProps.href, window.location.origin)
        : linkProps.href;

    const currentRolloutGroup = getRolloutGroup(pathname);
    const nextRolloutGroup = getRolloutGroup(href.pathname as string);
    if (currentRolloutGroup === nextRolloutGroup) {
      // No transition between rollout groups, so we can just navigate to the new page as normal
      return;
    }

    event.preventDefault();
    window.location.href = href.toString();
  };
  return <Link prefetch={false} {...linkProps} onClick={onClick} />;
}

function getRolloutGroup(pathname: string): string | null {
  return (
    rolloutGroupPaths.find((path) =>
      path.pathPrefixes.some((prefix) => pathname.startsWith(prefix)),
    )?.name ?? null
  );
}
